import React from 'react';
// import contentHelper from 'helpers/contentHelper';
import { Avatar, colors, Tooltip } from '@mui/material';
import parse from 'html-react-parser';
import {
  DraftManual,
  DraftManualItem,
  DraftPolicy,
  DraftPolicyItem,
  DraftPolicySection,
  DraftPolicySubSection,
  DraftPreface,
  DraftPrefaceItem,
  ManualContent,
} from 'views/App/Portal/Policy/_types/manuals/draft';
import _ from 'lodash';

export default class draftManualHelper {
  static getApprovedCount = (manual: DraftManualItem): number => {
    const policyApproved = manual.policyDraftStates?.APPROVED ?? 0;
    const prefaceApproved = manual.prefaceDraftStates?.APPROVED ?? 0;
    return policyApproved + prefaceApproved;
  };

  static getNumberManualNode = (node: any, manual: DraftManual) => {
    if (!manual || !node) return '';

    const chapterMap = new Map(
      manual.chapters.filter((ch) => ch.state !== 'DISABLED').map((chapter, index) => [chapter.id, index + 1])
    );
    const policyMap = new Map(
      manual.chapters
        .filter((ch) => ch.state !== 'DISABLED')
        .flatMap((chapter, chapterIndex) => {
          // Split policies into new and non-new, preserving original order within each group
          const nonNewPolicies = chapter.policies.filter((pol) => !pol.isNew && pol.state !== 'DISABLED');
          const newPolicies = chapter.policies.filter((pol) => pol.isNew);
          const sortedPolicies = [...nonNewPolicies, ...newPolicies];

          return sortedPolicies.map((policy, policyIndex) => [policy.id, `${(chapterIndex + 1) * 100 + policyIndex}`]);
        })
    );
    return chapterMap.get(node.id)?.toString() || policyMap.get(node.id) || '';
  };

  static formatManualNodeTitle = (node: any, manual: DraftManual, isSorting = false) => {
    const cleanTitle =
      node?.title
        ?.replace(/&amp;/g, '&')
        ?.replace(/&lt;/g, '<')
        ?.replace(/&gt;/g, '>')
        ?.replace(/&quot;/g, '"')
        ?.replace(/&#39;/g, "'") ?? '';

    const parsedTitle = parse(cleanTitle);
    const num = node ? this.getNumberManualNode(node, manual) : '';

    if (isSorting) {
      return `${num}: ${parsedTitle}`;
    }

    if (num?.length > 0) {
      return (
        <>
          {num}: {parsedTitle}
        </>
      );
    }
    return parsedTitle;
  };

  static getTitleByContentUri = (contentUri: string, manual: DraftManual) => {
    if (!manual || _.isEmpty(contentUri)) return '';

    let title: string | React.JSX.Element | React.JSX.Element[] = '';

    manual.prefaces.forEach((preface) => {
      if (preface.contentUri === contentUri) {
        title = preface.title;
      }
    });
    manual.chapters.forEach((chapter) => {
      if (chapter.contentUri === contentUri) {
        title = this.formatManualNodeTitle(chapter, manual);
      } else {
        chapter.policies.forEach((policy) => {
          if (policy.contentUri === contentUri) {
            title = this.formatManualNodeTitle(policy, manual);
          } else {
            const policyNumber: string = this.getNumberManualNode(policy, manual);
            (policy.sections || []).forEach((section: DraftPolicySection) => {
              if (section.contentUri === contentUri) {
                title = this.formatPolicyNodeTitle(section, policyNumber, policy);
              } else {
                (section.subSections || []).forEach((subSection: DraftPolicySubSection) => {
                  if (subSection.contentUri === contentUri) {
                    title = this.formatPolicyNodeTitle(subSection, policyNumber, policy);
                  }
                });
              }
            });
          }
        });
      }
    });
    return title;
  };

  static getNodeById = (id: string, manual: DraftManual) => {
    if (!manual || _.isEmpty(id)) return '';

    let resultNode: any | null = null;

    manual.prefaces.forEach((preface) => {
      if (preface.id === id) {
        resultNode = preface;
      }
    });
    manual.chapters.forEach((chapter) => {
      if (chapter.id === id) {
        resultNode = chapter;
      } else {
        chapter.policies.forEach((policy) => {
          if (policy.id === id) {
            resultNode = policy;
          } else {
            (policy.sections || []).forEach((section: DraftPolicySection) => {
              if (section.id === id) {
                resultNode = section;
              } else {
                (section.subSections || []).forEach((subSection: DraftPolicySubSection) => {
                  if (subSection.id === id) {
                    resultNode = subSection;
                  }
                });
              }
            });
          }
        });
      }
    });
    return resultNode;
  };

  static getNumberPolicyNode = (node: any, policy: DraftPolicy | DraftPolicyItem) => {
    let result = '';
    if (policy?.state !== 'DISABLED' && policy.sections) {
      // Split sections into new and non-new, preserving original order within each group
      const nonNewSections = policy.sections.filter((s) => !s.isNew && s.state !== 'DISABLED');
      const newSections = policy.sections.filter((s) => s.isNew);
      const sortedSections = [...nonNewSections, ...newSections];

      // Get the index of the current section
      const sectionIndex = sortedSections.findIndex((s) => s.id === node.id);
      if (sectionIndex !== -1) {
        result = `${sectionIndex + 1}`;
      } else {
        // If not a section, check if it's a subsection
        for (let i = 0; i < sortedSections.length; i++) {
          const section = sortedSections[i];
          // Split subsections into new and non-new, preserving original order within each group
          const nonNewSubSections = (section.subSections || []).filter((ss) => !ss.isNew && ss.state !== 'DISABLED');
          const newSubSections = (section.subSections || []).filter((ss) => ss.isNew);
          const sortedSubSections = [...nonNewSubSections, ...newSubSections];

          const subSectionIndex = sortedSubSections.findIndex((ss) => ss.id === node.id);
          if (subSectionIndex !== -1) {
            result = [i + 1, subSectionIndex + 1].join('.');
            break;
          }
        }
      }
    }
    return result;
  };

  static getManualContent = (manual: DraftManual) => {
    const manualContent: ManualContent[] = [];
    manual.prefaces.forEach((preface) => {
      manualContent.push({
        id: preface.id,
        title: this.formatManualNodeTitle(preface, manual),
        level: 'preface',
        contentUri: preface.contentUri,
      });
    });
    manual.chapters.forEach((chapter) => {
      chapter.policies.forEach((policy) => {
        const policyNumber = this.getNumberManualNode(policy, manual);
        manualContent.push({
          id: policy.id,
          title: this.formatManualNodeTitle(policy, manual),
          level: 'policy',
          contentUri: policy.contentUri,
        });
        (policy.sections ?? []).forEach((section) => {
          manualContent.push({
            id: section.id,
            title: this.formatPolicyNodeTitle(section, policyNumber, policy),
            level: 'section',
            contentUri: section.contentUri ?? '',
          });
          (section.subSections ?? []).forEach((subSection) => {
            manualContent.push({
              id: subSection.id,
              title: this.formatPolicyNodeTitle(subSection, policyNumber, policy),
              level: 'subsection',
              contentUri: subSection.contentUri ?? '',
            });
          });
        });
      });
    });
    return manualContent;
  };

  static formatPrefaceLockInfo = (preface: DraftPreface | DraftPrefaceItem) => {
    let result = '';
    if (preface.lock) {
      result = preface.lock.firstName
        ? [preface.lock.firstName, preface.lock.lastName].join(' ')
        : preface.lock.username;
    }
    return result;
  };

  static prefaceLockAvatar = (preface: DraftPreface | DraftPrefaceItem, compact = false) => {
    let result = '';
    if (preface.lock) {
      result = preface.lock.firstName
        ? [preface.lock.firstName, preface.lock.lastName]
          .filter((x) => x !== undefined)
          .map((x) => x![0].toLocaleUpperCase())
          .join('')
        : preface.lock.username[0].toLocaleUpperCase();
    } else {
      return null;
    }
    return (
      <Tooltip title={draftManualHelper.formatPrefaceLockInfo(preface)}>
        <Avatar
          color='info'
          sx={{
            width: compact ? 20 : 36,
            height: compact ? 20 : 36,
            backgroundColor: colors.deepPurple[500],
            color: '#FFF',
            fontSize: compact ? 10 : 18,
          }}
        >
          {result}
        </Avatar>
      </Tooltip>
    );
  };

  static formatPolicyNodeTitle = (node: any, policyNumber: string, policy: DraftPolicy | DraftPolicyItem) => {
    const cleanTitle =
      node?.title
        ?.replace(/<[^>]*>/g, '')
        ?.replace(/&amp;/g, '&')
        ?.replace(/&lt;/g, '<')
        ?.replace(/&gt;/g, '>')
        ?.replace(/&quot;/g, '"')
        ?.replace(/&#39;/g, "'") ?? '';

    const num = node ? this.getNumberPolicyNode(node, policy) : '';
    if (num?.length > 0) {
      return `${policyNumber}.${num}: ${cleanTitle}`;
    }
    return cleanTitle;
  };

  static formatPolicyLockInfo = (policy: DraftPolicy | DraftPolicyItem) => {
    let result = '';
    if (policy.lock) {
      result = policy.lock.firstName ? [policy.lock.firstName, policy.lock.lastName].join(' ') : policy.lock.username;
    }
    return result;
  };

  static policyLockAvatar = (policy: DraftPolicy | DraftPolicyItem, compact = false) => {
    let result = '';
    if (policy.lock) {
      result = policy.lock.firstName
        ? [policy.lock.firstName, policy.lock.lastName]
          .filter((x) => x !== undefined)
          .map((x) => x![0].toLocaleUpperCase())
          .join('')
        : policy.lock.username[0].toLocaleUpperCase();
    } else {
      return null;
    }
    return (
      <Tooltip title={draftManualHelper.formatPolicyLockInfo(policy)}>
        <Avatar
          sx={{
            width: compact ? 20 : 36,
            height: compact ? 20 : 36,
            backgroundColor: colors.deepPurple[500],
            color: '#FFF',
            fontSize: compact ? 10 : 18,
          }}
        >
          {result}
        </Avatar>
      </Tooltip>
    );
  };

  static insertSectionAfter = (sectionId: string, newId: string, policyData: DraftPolicy) => {
    const sectionIndex = policyData.sections.findIndex((section) => section.id === sectionId);

    let tag: 'FEDERAL' | 'STATE' | 'BEST_PRACTICE' | 'DISCRETIONARY' | 'AGENCY_CONTENT' | 'ACCREDITATION' =
      'AGENCY_CONTENT';
    if (policyData.manualLevel === 'GLOBAL') tag = 'FEDERAL';
    if (policyData.manualLevel === 'JURISDICTION') tag = 'STATE';

    const newSection: DraftPolicySection = {
      attachments: [],
      id: newId,
      title: 'Section Title',
      content: '',
      contentEditable: '',
      subSections: [],
      state: 'ENABLED',
      agencyModified: true,
      editLevelTag: tag,
      isNew: false,
      parentId: null,
    };
    const updatedSections = [
      ...policyData.sections.slice(0, sectionIndex + 1),
      newSection,
      ...policyData.sections.slice(sectionIndex + 1),
    ];

    return {
      ...policyData,
      sections: updatedSections,
    };
  };

  static insertSubSectionAfter = (sectionId: string, newId: string, policyData: DraftPolicy, subsectionId?: string) => {
    let section: DraftPolicySection | null = null;
    let sectionIndex = -1;
    let subSectionIndex = -1;
    policyData.sections.forEach((s, ind) => {
      if (s.id === sectionId) {
        section = s;
        sectionIndex = ind;
        if (subsectionId) {
          subSectionIndex = section.subSections?.findIndex((subsection) => subsection.id === subsectionId) ?? -1;
        }
      }
    });

    let tag: 'FEDERAL' | 'STATE' | 'BEST_PRACTICE' | 'DISCRETIONARY' | 'AGENCY_CONTENT' | 'ACCREDITATION' =
      'AGENCY_CONTENT';
    if (policyData.manualLevel === 'GLOBAL') tag = 'FEDERAL';
    if (policyData.manualLevel === 'JURISDICTION') tag = 'STATE';

    const newSubSection: DraftPolicySubSection = {
      attachments: [],
      id: newId,
      title: 'Subsection Title',
      content: '',
      contentEditable: '',
      state: 'ENABLED',
      agencyModified: true,
      editLevelTag: tag,
      isNew: false,
      parentId: sectionId ?? null,
    };
    const updatedSections = [
      ...policyData.sections.slice(0, sectionIndex),
      {
        ...policyData.sections[sectionIndex],
        subSections: [
          ...(policyData.sections[sectionIndex].subSections ?? []).slice(0, subSectionIndex + 1),
          newSubSection,
          ...(policyData.sections[sectionIndex].subSections ?? []).slice(subSectionIndex + 1),
        ],
      },
      ...policyData.sections.slice(sectionIndex + 1),
    ];

    return {
      ...policyData,
      sections: updatedSections,
    };
  };

  static getEnabledSectionIndex = (sectionId: string, policy: DraftPolicy) => {
    const enabledSections = policy.sections.filter((section) => section.state !== 'DISABLED');
    return enabledSections.findIndex((section) => section.id === sectionId) + 1;
  };

  static getEnabledSubSectionIndex(sectionId: string, subSectionId: string, policy: DraftPolicy) {
    const section = policy.sections.find((section) => section.id === sectionId);

    if (!section) return null;

    const enabledSubSections = (section.subSections ?? []).filter((subSection) => subSection.state !== 'DISABLED');
    return enabledSubSections.findIndex((subSection) => subSection.id === subSectionId) + 1;
  }
}
